import React, { useMemo, useState } from 'react';
import {
  Briefcase, Cake, CalendarDays, ChevronLeft, ChevronRight, Flag,
} from 'lucide-react';
import api from '../../utils/axios';
import { companyToday } from '../../utils/dateUtils';
import useCardData from './useCardData';
import { DashboardCard, CardError, CardEmpty } from './DashboardCard';
import { formatLongDay, formatShortDay, fullName } from './format';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_LISTED = 6;

const TYPE_STYLE = {
  holiday: {
    label: 'Holiday', icon: Flag, dot: 'bg-danger-500', item: 'border-danger-500 bg-danger-50', iconClass: 'text-danger-600',
  },
  birthday: {
    label: 'Birthday', icon: Cake, dot: 'bg-warning-500', item: 'border-warning-500 bg-warning-50', iconClass: 'text-warning-600',
  },
  anniversary: {
    label: 'Work anniversary', icon: Briefcase, dot: 'bg-success-500', item: 'border-success-500 bg-success-50', iconClass: 'text-success-600',
  },
};

/** 'YYYY-MM' moved by n months. */
export function shiftMonth(month, n) {
  const [year, monthNumber] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year, monthNumber - 1 + n, 1));
  return date.toISOString().slice(0, 7);
}

/**
 * The month's grid as weeks of 'YYYY-MM-DD' (null for the padding before the 1st and
 * after the last day), Monday first. Calendar arithmetic in UTC, so it is the same in
 * every browser zone.
 */
export function monthGrid(month) {
  const [year, monthNumber] = month.split('-').map(Number);
  const daysInMonth = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const leading = (new Date(Date.UTC(year, monthNumber - 1, 1)).getUTCDay() + 6) % 7;
  const cells = Array.from({ length: leading }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(`${month}-${String(day).padStart(2, '0')}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let index = 0; index < cells.length; index += 7) weeks.push(cells.slice(index, index + 7));
  return weeks;
}

const monthTitle = (month) => new Date(`${month}-01T00:00:00Z`)
  .toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

export function eventTitle(event) {
  if (event.type === 'holiday') return event.title;
  if (event.type === 'birthday') return `${fullName(event)}'s birthday`;
  return `${fullName(event)} - ${event.years} ${event.years === 1 ? 'year' : 'years'}`;
}

const EventItem = ({ event }) => {
  const style = TYPE_STYLE[event.type];
  const Icon = style.icon;
  return (
    <li
      data-testid="dashboard-events-item"
      className={`flex items-center gap-3 rounded-lg border-l-4 px-3 py-2 ${style.item}`}
    >
      <Icon className={`h-4 w-4 flex-shrink-0 ${style.iconClass}`} aria-hidden="true" />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-gray-900 truncate">{eventTitle(event)}</span>
        <span className="block text-xs text-gray-600 truncate">
          {`${style.label} · ${formatShortDay(event.date)}`}
        </span>
      </span>
    </li>
  );
};

/**
 * A month calendar of company holidays and the team's birthdays and work anniversaries
 * (GET /api/dashboard/events). Picking a day lists that day's events and the rest of the
 * month after it.
 */
const EventsCard = ({ className = '' }) => {
  const today = companyToday();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [selected, setSelected] = useState(today);
  const result = useCardData(
    async () => (await api.get('/api/dashboard/events', { params: { month } })).data.data,
    [month]
  );

  const byDay = useMemo(() => {
    const map = new Map();
    for (const event of (result.data ? result.data.events : [])) {
      if (!map.has(event.date)) map.set(event.date, []);
      map.get(event.date).push(event);
    }
    return map;
  }, [result.data]);

  // The list starts at the picked day when it is in this month, else at the 1st.
  const from = selected.slice(0, 7) === month ? selected : `${month}-01`;
  const listed = result.data ? result.data.events.filter((event) => event.date >= from) : [];

  const goTo = (n) => {
    const next = shiftMonth(month, n);
    setMonth(next);
    setSelected(next === today.slice(0, 7) ? today : `${next}-01`);
  };

  const navButton = 'p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

  return (
    <DashboardCard
      data-testid="dashboard-events"
      title="Events"
      subtitle={monthTitle(month)}
      icon={CalendarDays}
      className={className}
      action={(
        <>
          <button type="button" className={navButton} onClick={() => goTo(-1)} aria-label="Previous month" data-testid="dashboard-events-prev">
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button type="button" className={navButton} onClick={() => goTo(1)} aria-label="Next month" data-testid="dashboard-events-next">
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </>
      )}
    >
      <table className="w-full table-fixed text-center" aria-label={`Events in ${monthTitle(month)}`}>
        <thead>
          <tr>
            {WEEKDAYS.map((weekday) => (
              <th key={weekday} scope="col" className="pb-2 text-xs font-medium text-gray-500">{weekday}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {monthGrid(month).map((week) => (
            <tr key={week.find(Boolean)}>
              {week.map((day, index) => {
                if (!day) return <td key={`pad-${index}`} />;
                const events = byDay.get(day) || [];
                const isSelected = day === selected;
                const isToday = day === today;
                const types = [...new Set(events.map((event) => event.type))];
                let tone = 'text-gray-700 hover:bg-gray-100';
                if (isSelected) tone = 'bg-primary-600 text-white hover:bg-primary-700';
                else if (isToday) tone = 'text-primary-700 ring-1 ring-inset ring-primary-300 hover:bg-primary-50';
                return (
                  <td key={day} className="py-0.5">
                    <button
                      type="button"
                      onClick={() => setSelected(day)}
                      aria-pressed={isSelected}
                      aria-label={`${formatLongDay(day)}${events.length ? `, ${events.length} ${events.length === 1 ? 'event' : 'events'}` : ''}`}
                      data-testid={`dashboard-events-day-${day}`}
                      className={`relative mx-auto flex h-9 w-9 flex-col items-center justify-center rounded-full text-sm tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${tone} ${isToday ? 'font-semibold' : ''}`}
                    >
                      {Number(day.slice(8, 10))}
                      {types.length > 0 && (
                        <span className="absolute bottom-1 flex gap-0.5" aria-hidden="true">
                          {types.map((type) => (
                            <span
                              key={type}
                              className={`h-1 w-1 rounded-full ${isSelected ? 'bg-white' : TYPE_STYLE[type].dot}`}
                            />
                          ))}
                        </span>
                      )}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500" aria-hidden="true">
        {Object.entries(TYPE_STYLE).map(([type, style]) => (
          <span key={type} className="inline-flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${style.dot}`} />
            {style.label}
          </span>
        ))}
      </div>

      <div className="mt-4 border-t border-gray-100 pt-4">
        <h3 className="text-sm font-semibold text-gray-900" data-testid="dashboard-events-from">
          {from === today ? 'Today and later this month' : `From ${formatLongDay(from)}`}
        </h3>
        <div className="mt-3">
          {result.loading && (
            <div role="status" aria-label="Loading events" className="space-y-2 animate-pulse">
              <div className="h-12 bg-gray-100 rounded-lg" />
              <div className="h-12 bg-gray-100 rounded-lg" />
            </div>
          )}
          {result.error && (
            <CardError
              data-testid="dashboard-events-error"
              message="Could not load events."
              onRetry={result.reload}
            />
          )}
          {result.data && listed.length === 0 && (
            <CardEmpty icon={CalendarDays} data-testid="dashboard-events-empty">
              No holidays, birthdays or work anniversaries for the rest of this month.
            </CardEmpty>
          )}
          {result.data && listed.length > 0 && (
            <>
              <ul className="space-y-2">
                {listed.slice(0, MAX_LISTED).map((event) => (
                  <EventItem key={`${event.type}-${event.date}-${event.employee_id || event.title}`} event={event} />
                ))}
              </ul>
              {listed.length > MAX_LISTED && (
                <p className="mt-2 text-xs text-gray-500">{`and ${listed.length - MAX_LISTED} more this month`}</p>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardCard>
  );
};

export default EventsCard;
